import { useLocation, useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useMemo } from "react";
import { usePermission } from "../../../hooks/use-permission";
import useUserStore from "../../../store/user";
import logo from "../../../assets/images/logo-georanking.png";
import { ADMIN_NAV_GROUPS, USER_TYPE_LABEL, adminTitleFor } from "../admin-nav/items";
import { useNovidadesNaoVistas } from "../../../modules/novidades/vistas";
import {
  Avatar,
  Breadcrumb,
  ContadorNovidades,
  GroupTitle,
  Logo,
  LogoutButton,
  NavLink,
  NovidadesNaBarra,
  Rail,
  TopBar,
  UserCard,
  UserText,
} from "./styles";

const initials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "GR";

/**
 * Menu lateral fixo do admin (redesign V3, fase 2). As rotas e as regras de
 * quem vê cada item são as mesmas do menu antigo; só a forma mudou.
 */
export const AdminRail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccess, hasRole } = usePermission();
  const { user, clearUser } = useUserStore();
  const novidades = useNovidadesNaoVistas(user?.name).length;

  const groups = useMemo(
    () =>
      ADMIN_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!canAccess(item.path)) return false;
          // Backlinks é uma tela de conta não-admin, como sempre foi.
          if (item.path === "/backlinks-dashboard" && hasRole("ADMIN")) return false;
          return true;
        }),
      })).filter((group) => group.items.length > 0),
    [canAccess, hasRole],
  );

  const go = (path: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    navigate(path);
  };

  const handleLogout = () => {
    clearUser();
    navigate("/login");
  };

  return (
    <Rail aria-label="Menu do admin">
      <Logo href="/admin" onClick={go("/admin")} aria-label="GeoRanking, início">
        <img src={logo} alt="GeoRanking" />
      </Logo>
      {groups.map((group) => (
        <div key={group.title} style={{ display: "contents" }}>
          <GroupTitle>{group.title}</GroupTitle>
          {group.items.map((item) => {
            const active = location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                href={item.path}
                onClick={go(item.path)}
                $active={active}
                aria-current={active ? "page" : undefined}
              >
                {item.icon}
                {item.label}
                {item.path === "/novidades" && novidades > 0 && (
                  <ContadorNovidades aria-hidden="true">{novidades}</ContadorNovidades>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
      <UserCard>
        <Avatar aria-hidden="true">{initials(user?.name)}</Avatar>
        <UserText>
          <span className="name">{user?.name || "Usuário"}</span>
          <span className="role">{USER_TYPE_LABEL[user?.userType ?? ""] ?? "GeoRanking"}</span>
        </UserText>
        <LogoutButton type="button" onClick={handleLogout} aria-label="Sair" title="Sair">
          <LogoutIcon sx={{ fontSize: 20 }} />
        </LogoutButton>
      </UserCard>
    </Rail>
  );
};

/** Barra superior: onde o usuário está. Busca global e chip de sincronização ficaram de fora (sem fonte de dado). */
export const AdminTopBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const novas = useNovidadesNaoVistas(user?.name);
  const title = adminTitleFor(location.pathname);
  const naTelaDeNovidades = location.pathname.startsWith("/novidades");
  return (
    <TopBar>
      <Breadcrumb aria-label="Você está em">
        <span>Admin</span>
        {title && (
          <>
            <ChevronRightIcon />
            <span className="current">{title}</span>
          </>
        )}
      </Breadcrumb>
      {novas.length > 0 && !naTelaDeNovidades && (
        <NovidadesNaBarra type="button" onClick={() => navigate("/novidades")}>
          <span className="material-icons" aria-hidden="true">campaign</span>
          {novas.length === 1 ? `Novidade: ${novas[0].titulo}` : `${novas.length} novidades na ferramenta`}
        </NovidadesNaBarra>
      )}
    </TopBar>
  );
};
